/**
 * Date Utilities
 * ──────────────
 * Shared date formatting and delivery date calculations for cart/product pages.
 * Extracted to eliminate duplication across mini-cart-header.js, cart-template.js, etc.
 *
 * One rule for every shipping promise on the site (audit 2026-10-04, L6-10 / L8-3 / L8-4):
 * an order ships today only before 4:00 PM Eastern on a weekday; otherwise the first
 * ship day is the next business day. Handling lead times and the 2-day transit window
 * are counted in business days (Saturday and Sunday skipped). snippets/_c-ship-window.liquid
 * applies the same rule server-side; this file refines it in the browser.
 */

/**
 * Format a date as "Month Day(suffix)" (e.g., "July 10th")
 * @param {Date} date - Date object to format
 * @returns {string} Formatted date string
 */
function formatDate(date) {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const d = date.getDate();
  let suffix = 'th';
  if (d === 1 || d === 21 || d === 31) suffix = 'st';
  else if (d === 2 || d === 22) suffix = 'nd';
  else if (d === 3 || d === 23) suffix = 'rd';
  return months[date.getMonth()] + ' ' + d + suffix;
}

/**
 * Transit days added on top of the handling lead time to get an arrival window.
 */
const TRANSIT_DAYS = 2;

/**
 * Same-day shipping cutoff, hour of the day in the warehouse time zone (4:00 PM Eastern).
 */
const SHIP_CUTOFF_HOUR = 16;
const SHIP_TIME_ZONE = 'America/New_York';

/**
 * Current wall-clock time in the warehouse time zone, as a Date whose local fields
 * (getHours, getDay, ...) read as Eastern time.
 * @returns {Date}
 */
function getWarehouseNow() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: SHIP_TIME_ZONE }));
}

/**
 * @param {Date} date
 * @returns {boolean} true Monday to Friday
 */
function isBusinessDay(date) {
  const day = date.getDay();
  return day >= 1 && day <= 5;
}

/**
 * Add business days to a date (weekends skipped). n = 0 returns a copy of the date.
 * @param {Date} date
 * @param {number} n
 * @returns {Date}
 */
function addBusinessDays(date, n) {
  const result = new Date(date);
  let remaining = Math.max(0, Math.floor(Number(n) || 0));
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    if (isBusinessDay(result)) remaining -= 1;
  }
  return result;
}

/**
 * Same-day shipping state for an order placed right now.
 * @returns {{now: Date, canShipToday: boolean, firstShipDate: Date, hours: number, minutes: number}}
 *   firstShipDate is today while the cutoff is open on a weekday, else the next business day
 *   (midnight); hours/minutes count down to the cutoff when canShipToday is true.
 */
function getShipTodayState() {
  const now = getWarehouseNow();
  const cutoff = new Date(now);
  cutoff.setHours(SHIP_CUTOFF_HOUR, 0, 0, 0);
  const canShipToday = isBusinessDay(now) && now < cutoff;

  let firstShipDate = new Date(now);
  firstShipDate.setHours(0, 0, 0, 0);
  if (!canShipToday) firstShipDate = addBusinessDays(firstShipDate, 1);

  const ms = canShipToday ? cutoff - now : 0;
  return {
    now: now,
    canShipToday: canShipToday,
    firstShipDate: firstShipDate,
    hours: Math.floor(ms / 3600000),
    minutes: Math.floor((ms % 3600000) / 60000)
  };
}

/**
 * Get delivery date range (e.g., "July 10th - July 12th")
 * Window start = first ship day + leadMin business days (the unit ships on its last
 * handling day and arrives the next business day); window end = first ship day +
 * leadMax + TRANSIT_DAYS business days. Mirrors snippets/_c-ship-window.liquid.
 * @param {number} leadMin - Minimum handling lead time in days
 * @param {number} leadMax - Maximum handling lead time in days
 * @returns {string} Date range string
 */
function getDeliveryDates(leadMin, leadMax) {
  const min = Number(leadMin) > 0 ? Number(leadMin) : 1;
  const max = Number(leadMax) >= min ? Number(leadMax) : min;
  const base = getShipTodayState().firstShipDate;
  const start = addBusinessDays(base, min);
  const end = addBusinessDays(base, max + TRANSIT_DAYS);
  return formatDate(start) + ' - ' + formatDate(end);
}

/**
 * Get shipping countdown text based on current time and 4 PM NY cutoff
 * @returns {string} "Order within Xh Ym to ship today" or "Order for next-day shipment."
 */
function getShipTodayCountdownText() {
  const state = getShipTodayState();
  return state.canShipToday
    ? 'Order within ' + state.hours + 'h ' + state.minutes + 'm to ship today'
    : 'Order for next-day shipment.';
}

/**
 * Show or hide every [data-ships-today-label] element (the "Ships today" label on the
 * PDP variant tiles): visible only while an order placed now ships today. Liquid renders
 * the initial state from the store clock; this keeps it right as the clock moves.
 * The label element must not carry .desktop-only or .mobile-only (their !important display
 * would beat this inline toggle); snippets/pdp-product_info.liquid wraps it in a plain span.
 */
function updateShipsTodayLabels() {
  const show = getShipTodayState().canShipToday;
  document.querySelectorAll('[data-ships-today-label]').forEach((el) => {
    el.style.display = show ? '' : 'none';
  });
}

/**
 * Refresh all delivery date displays on the page
 * Each [data-delivery-estimate] element carries its own lead time from Liquid.
 * formatDate() writes English month names, so on a storefront whose <html lang> is not
 * English an element that already holds its Liquid-rendered (translated) window is left
 * alone (review R3); empty ones (the cart widget renders none server-side) are still filled.
 */
function refreshAllDeliveryDates() {
  const lang = String(document.documentElement.getAttribute('lang') || 'en').toLowerCase();
  const englishPage = lang === 'en' || lang.indexOf('en-') === 0;
  document.querySelectorAll('[data-delivery-estimate]').forEach((el) => {
    if (!englishPage && el.textContent.trim() !== '') return;
    el.textContent = getDeliveryDates(
      el.getAttribute('data-lead-min'),
      el.getAttribute('data-lead-max')
    );
  });
  updateAllShipTodayCountdowns();
}

/**
 * Update all .ship-today-countdown elements on the page
 */
function updateAllShipTodayCountdowns() {
  const text = getShipTodayCountdownText();
  document.querySelectorAll('.ship-today-countdown').forEach((el) => {
    el.textContent = text;
  });
  updateShipsTodayLabels();
}
